import React from "react";
import { Loader2 } from "lucide-react";
import { usePermissions } from "@/lib/usePermissions";
import Forbidden from "./Forbidden";

/**
 * RequirePermission — route-level RBAC guard.
 *
 * Wraps a page so that authenticated users WITHOUT the required permission
 * see a <Forbidden> state instead of the page content. They are NOT logged
 * out and NOT redirected to /login: the session is preserved.
 *
 * Auth (401) is handled upstream by <ProtectedRoute>. This guard handles
 * authorization (403) at the UI layer.
 *
 * Usage:
 *   <Route path="/hotel/payments" element={
 *     <RequirePermission perm="manage_payments">
 *       <HotelPayments />
 *     </RequirePermission>
 *   } />
 *
 *   <RequirePermission anyOf={["view_audit","manage_team"]}>...</RequirePermission>
 */
export default function RequirePermission({
  perm,
  anyOf,
  allOf,
  forbiddenTitle,
  forbiddenMessage,
  ctaTo,
  ctaLabel,
  children,
}) {
  const { loading, can, canAny, canAll } = usePermissions();

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center" data-testid="permission-guard-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#102A43]" />
      </div>
    );
  }

  let allowed = true;
  let missing = null;
  if (perm) {
    allowed = can(perm);
    missing = perm;
  } else if (anyOf?.length) {
    allowed = canAny(anyOf);
    missing = anyOf.join(" / ");
  } else if (allOf?.length) {
    allowed = canAll(allOf);
    missing = allOf.join(" + ");
  }

  if (!allowed) {
    return (
      <Forbidden
        title={forbiddenTitle}
        message={forbiddenMessage}
        ctaTo={ctaTo}
        ctaLabel={ctaLabel}
        missingPermission={missing}
      />
    );
  }

  return children;
}
